import {
  DownloadOutlined,
  ImportOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { t } from "i18next";
import * as React from "react";
import { observer } from "mobx-react-lite";
import { DropdownChildButton } from "src/components/buttons/DropdownChildButton";
import {
  Alert,
  Button,
  ButtonProps,
  Col,
  Flex,
  Form,
  Modal,
  notification,
  Row,
  Space,
  Spin,
  Upload,
} from "antd";
import axios from "axios";
import { apiObjUrl } from "src/common/apiUrl";
import { langHdr } from "src/lang/langHdr";
import { classNames } from "src/common/classNames";
import { ZUid } from "src/types/zUid";
import { EntityImportButtonStore } from "./EntityImportButtonStore";
import styles from "./EntityImportButton.module.less";
import { FileType } from "./types";

const accept =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"; // Microsoft Excel (XLSX);
const maxSize = 50;

const calculateTotalSize = (size: number) => size / (1024 * 1024);

const createTemplateUrl = (objectId: ZUid) =>
  axios.getUri({
    url: apiObjUrl(`/objects/${objectId}/file-template`),
    headers: langHdr(),
  });

interface PropsEntityImportButton {
  objectId: ZUid;
  objectName?: string;
  disabled?: boolean;
}

export const EntityImportButton: React.FC<PropsEntityImportButton> = observer(
  (props) => {
    const { objectId, objectName, disabled } = props;
    const [fileList, setFileList] = React.useState<FileType[]>([]);
    const store = React.useMemo(
      () => new EntityImportButtonStore(objectId),
      [objectId],
    );

    React.useEffect(() => {
      store.init();
    }, []);

    const isImportInProgress = store.importStatus?.status === "PROCESS";
    const isLoading = store.loading || store.checkingStatus;

    const handleRemove = () => {
      store.setResult(null);
      setFileList([]);
    };

    const handleBeforeUpload = (file: FileType) => {
      const totalSize = calculateTotalSize(file.size || 0);
      if (totalSize > maxSize) {
        notification.error({
          message: t("Total files size exceeds", { size: maxSize }),
        });
      } else {
        store.setResult(null);
        setFileList([file]);
      }
      return false;
    };

    const onFinish = async () => {
      if (!fileList[0]) return;
      await store.uploadFile(fileList[0]);
      setFileList([]);
    };

    const handleModalOpen = () => {
      store.checkCurrentImportStatus();
      store.setOpen(true);
    };

    const alertInfo = (
      <Space direction="vertical">
        <Button
          block
          icon={<DownloadOutlined />}
          onClick={(e) => e.stopPropagation()}
          download={`${objectName ?? objectId}.xlsx`}
          href={createTemplateUrl(objectId)}
        >
          {t("Download template")}
        </Button>
        <Alert
          message={
            <div>
              <div>{t("Import from file is running")}</div>
              <div>
                {t("Object")}: {store.importStatus?.objectName}
              </div>
              <div>
                {t("File")}: {store.importStatus?.fileName}
              </div>
              <div>
                {t(
                  "Import from file will be available again after the current import is completed",
                )}
                .
              </div>
            </div>
          }
          type="info"
          showIcon
        />
      </Space>
    );

    const getModalContent = () => {
      if (isLoading) return <Spin />;
      if (isImportInProgress) return alertInfo;

      const okButtonProps: ButtonProps = isImportInProgress
        ? {
            htmlType: "button",
            loading: store.loading,
            type: "primary",
          }
        : {
            htmlType: "submit",
            type: "primary",
            disabled: !fileList[0] || isImportInProgress,
            loading: store.loading,
          };
      const okText = isImportInProgress ? t("OK") : t("Import");

      return (
        <Form
          className={styles.form}
          disabled={store.loading}
          layout="vertical"
          onFinish={onFinish}
        >
          <Flex gap={8} vertical>
            <Form.Item name="files" label={t("Attached file")}>
              <Upload
                fileList={fileList}
                className={styles.uploader}
                maxCount={1}
                accept={accept}
                multiple={false}
                beforeUpload={handleBeforeUpload}
                onRemove={handleRemove}
              >
                <Row align="bottom" gutter={8}>
                  <Col span={12}>
                    <Button
                      block
                      className={styles.importButton}
                      icon={<UploadOutlined />}
                    >
                      {t("Upload")}
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      icon={<DownloadOutlined />}
                      onClick={(e) => e.stopPropagation()}
                      download={`${objectName ?? objectId}.xlsx`}
                      href={createTemplateUrl(objectId)}
                    >
                      {t("Download template")}
                    </Button>
                  </Col>
                </Row>
              </Upload>
            </Form.Item>
            <span className={styles.textInfo}>
              {t("Format: value", { value: "xlsx" })}.&nbsp;
              {t("Maximum total size:", { size: maxSize })}
            </span>
            {!store.result && (
              <span className={styles.textTip}>
                {t("File processing may take a long time")}
              </span>
            )}
            {store.result?.status === "error" && (
              <span className={classNames([styles.textTip, styles.textError])}>
                {t("File upload error")}. {store.result.description}
              </span>
            )}
            <Flex gap={15} justify="end">
              <Button htmlType="button" onClick={() => store.setOpen(false)}>
                Отмена
              </Button>
              <Button htmlType="submit" {...okButtonProps}>
                {okText}
              </Button>
            </Flex>
          </Flex>
        </Form>
      );
    };

    return (
      <>
        <DropdownChildButton
          icon={<ImportOutlined />}
          disabled={disabled}
          onClick={handleModalOpen}
        >
          {t("Import from file")}
        </DropdownChildButton>
        <Modal
          title={t("Import from file")}
          open={store.open}
          closable
          footer={null}
          onCancel={() => store.setOpen(false)}
        >
          {getModalContent()}
        </Modal>
      </>
    );
  },
);
